import { CardHeader, Empty, HasPermission, Loading } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PermissionName } from "../../../../../shared/types/permission";
import { birthdayWidgetService } from "../../../dashboard/widgets/birthdays/service";
import { SimplePagination } from "../../../dashboard/widgets/birthdays/SimplePagination";
import { DurationSubtitle } from "../../../dashboard/widgets/common/DurationSubtitle";
import { LoadingOverlay } from "../../../dashboard/widgets/common/LoadingOverlay";
import { StyledList } from "../../../dashboard/widgets/common/StyledList";
import { ViewAllListItemTitle } from "../../../dashboard/widgets/common/ViewAllListItemTitle";
import { contactDatabaseSettingsService } from "../../../shared/api/contactDatabaseSettingsService";
import { List, ListItem, ListItemLink, ListItemTrailing } from "../../../shared/ui/list/listComponents";
import { ListItemTitle } from "../../../shared/ui/list/ListItemTitle";
import { StyledCard, StyledCardBody } from "../common/StyledCard";
import { BirthdateDistance } from "./BirthdateDistance";

const getCurrentYearsBirthday = (dateOfBirth: string): string => {
	const currentYear = new Date().getFullYear();
	const splitDate = dateOfBirth.split("-");
	return `${currentYear}-${splitDate[1]}-${splitDate[2]}`;
};

const PaginationWrapper = styled.div`
	margin-top: auto;
	padding: ${({ theme }) => theme.spacing(2)};
`;

const EmptyContainer = styled.div`
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
`;

export const BirthdaysWidget = () => {
	const { t } = useTranslation();
	const [page, setPageNumber] = useState<number>(1);
	const { data, isLoading } = useQuery({
		queryKey: birthdayWidgetService.key.concat(`${page}`),
		queryFn: () => birthdayWidgetService.index(page).then((x) => x.data),
	});
	const { data: contactDatabaseSettings, isLoading: settingsIsLoading } = useQuery({
		queryKey: contactDatabaseSettingsService.keys.getSettings,
		queryFn: () => contactDatabaseSettingsService.getSettings().then((x) => x.data.data),
	});
	const content = useMemo(() => data?.data || [], [data?.data]);

	return (
		<StyledCard>
			<CardHeader>{t("Upcoming Birthdays")}</CardHeader>
			<StyledCardBody>
				{content.length === 0 && (
					<EmptyContainer>
						<Empty>{t("No data available.")}</Empty>
					</EmptyContainer>
				)}
				<List>
					{content.map((x) => (
						<ListItem key={x.id}>
							<ListItemLink to={`/contacts/active/${x.id}`}>
								<ListItemTitle>
									{x.firstName} {x.lastName}
									<DurationSubtitle duration={{ start: x.dateOfBirth, end: x.dateOfBirth }} />
								</ListItemTitle>
								<ListItemTrailing>
									<BirthdateDistance date={getCurrentYearsBirthday(x.dateOfBirth)} />
								</ListItemTrailing>
							</ListItemLink>
						</ListItem>
					))}
				</List>
				{contactDatabaseSettings?.dateOfBirthDataField === null && (
					<StyledList>
						<HasPermission<PermissionName> permissions={["contactDatabaseSettings.read"]}>
							<ListItem>
								<ListItemLink to="/contact-settings">
									<ViewAllListItemTitle>{t("No Birthday Datafield defined.")}</ViewAllListItemTitle>
								</ListItemLink>
							</ListItem>
						</HasPermission>
					</StyledList>
				)}
				{!!content.length && (
					<PaginationWrapper>
						<SimplePagination
							onChange={setPageNumber}
							pagination={{
								page,
								total: data?.meta?.total || 0,
								from: data?.meta?.from || 0,
								to: data?.meta?.to || 0,
								last: data?.meta?.last_page || 0,
							}}
						/>
					</PaginationWrapper>
				)}
			</StyledCardBody>
			{(isLoading || settingsIsLoading) && (
				<LoadingOverlay>
					<Loading />
				</LoadingOverlay>
			)}
		</StyledCard>
	);
};
